/**
 * 슬래시 메뉴 (P1-④) — `/` 입력 시 블록 타입 선택 팝업.
 * 샘플 block-editor.html의 슬래시 패턴 이식.
 *
 *  - 트리거: 문단 시작이 `/` + 공백없는 질의(`^\/(\S*)$`)일 때 열림/필터.
 *  - 키보드: ArrowUp/Down 이동, Enter 적용, Escape 닫기 — **capture 단계에서 가로채
 *    stopPropagation** → editing.js의 Enter(분할) 핸들러보다 우선(메뉴 열렸을 때만).
 *  - 적용: 현재 문단을 선택 타입으로 변환(`/질의` 텍스트 소거).
 *  - IME 가드: 조합 중 미동작.
 */

import { changeBlockType, propsForType } from "../editing.js";
import { BLOCK_ICON } from "./icons.js";

// [type, level, locale 키, 기본 라벨, 키워드(영문 별칭 포함), 그룹, 아이콘 글리프, 부제목, 단축키 배지]
// blocknote 정합: 제목(groupHeading)을 먼저, 나머지(groupBasic)를 뒤에.
const ITEM_DEFS = [
  { type: "heading", level: 1, key: "heading1", def: "제목1", kw: "h1 heading 제목 title 대제목", group: "groupHeading", ico: "H1", sub: "섹션 제목 (대)", sc: "Ctrl+Alt+1" },
  { type: "heading", level: 2, key: "heading2", def: "제목2", kw: "h2 heading 제목 title 중제목", group: "groupHeading", ico: "H2", sub: "섹션 제목 (중)", sc: "Ctrl+Alt+2" },
  { type: "heading", level: 3, key: "heading3", def: "제목3", kw: "h3 heading 제목 title subheading", group: "groupHeading", ico: "H3", sub: "섹션 제목 (소)", sc: "Ctrl+Alt+3" },
  { type: "heading", level: 4, key: "heading4", def: "제목4", kw: "h4 heading 제목 title 소제목", group: "groupHeading", ico: "H4", sub: "하위 소단락 제목" },
  { type: "heading", level: 5, key: "heading5", def: "제목5", kw: "h5 heading 제목 title 소제목", group: "groupHeading", ico: "H5", sub: "작은 하위 섹션 제목" },
  { type: "heading", level: 6, key: "heading6", def: "제목6", kw: "h6 heading 제목 title 소제목", group: "groupHeading", ico: "H6", sub: "가장 하위 수준 제목" },
  { type: "heading", level: 1, isToggle: true, key: "toggleHeading1", def: "접을 수 있는 제목1", kw: "toggle 접기 토글 heading 제목 h1 대제목 collapsible", group: "groupHeading", ico: "▸H", sub: "내용을 접을 수 있는 섹션 제목(대)" },
  { type: "heading", level: 2, isToggle: true, key: "toggleHeading2", def: "접을 수 있는 제목2", kw: "toggle 접기 토글 heading 제목 h2 중제목 collapsible", group: "groupHeading", ico: "▸H", sub: "내용을 접을 수 있는 섹션 제목(중)" },
  { type: "heading", level: 3, isToggle: true, key: "toggleHeading3", def: "접을 수 있는 제목3", kw: "toggle 접기 토글 heading 제목 h3 소제목 collapsible", group: "groupHeading", ico: "▸H", sub: "내용을 접을 수 있는 섹션 제목(소)" },
  { type: "paragraph", key: "paragraph", def: "텍스트", kw: "text 텍스트 본문 paragraph p", group: "groupBasic", ico: "¶", sub: "일반 텍스트", sc: "Ctrl+Alt+0" },
  { type: "bulletListItem", key: "bulletListItem", def: "글머리 기호", kw: "bullet ul 글머리 목록 list 불릿", group: "groupBasic", ico: "•", sub: "간단한 글머리 기호 목록", sc: "Ctrl+Shift+8" },
  { type: "numberedListItem", key: "numberedListItem", def: "번호 매기기", kw: "number ol 번호 목록 list", group: "groupBasic", ico: "1.", sub: "번호가 매겨진 목록", sc: "Ctrl+Shift+7" },
  { type: "checkListItem", key: "checkListItem", def: "할 일", kw: "todo check 체크 할일 task 체크리스트", group: "groupBasic", ico: "☑", sub: "체크박스가 있는 목록", sc: "Ctrl+Shift+9" },
  { type: "toggleListItem", key: "toggleList", def: "접을 수 있는 목록", kw: "toggle 접기 토글 목록 list collapsible 접을수있는", group: "groupBasic", ico: "▸", sub: "숨길 수 있는 하위 항목 목록", sc: "Ctrl+Shift+6" },
  { type: "quote", key: "quote", def: "인용", kw: "quote 인용 blockquote", group: "groupBasic", ico: "❝", sub: "인용문 또는 발췌" },
  { type: "codeBlock", key: "codeBlock", def: "코드", kw: "code 코드 codeblock 코드블록", group: "groupBasic", ico: "</>", sub: "구문 강조 코드 블록" },
];
function esc(s) {
  return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function buildBaseItems(L) {
  const s = (L && L.slashMenu) || {};
  const g = (k) => s[k] || k;
  return ITEM_DEFS.map((d) => ({ type: d.type, level: d.level, isToggle: d.isToggle, label: s[d.key] || d.def, kw: d.kw, group: g(d.group), ico: d.ico, sub: (s[d.key + "Sub"] || d.sub), sc: d.sc }));
}

export function attachSlashMenu(editorEl, opts) {
  opts = opts || {};
  const isComposing = typeof opts.isComposing === "function" ? opts.isComposing : () => false;
  const onApply = typeof opts.onApply === "function" ? opts.onApply : () => {};
  const extraItems = Array.isArray(opts.extraItems) ? opts.extraItems : [];
  const headingLevels = Array.isArray(opts.headingLevels) && opts.headingLevels.length ? opts.headingLevels : [1, 2, 3, 4, 5, 6];
  const baseItems = buildBaseItems(opts.locale).filter((it) => it.type !== "heading" || headingLevels.includes(it.level));

  const menu = document.createElement("div");
  menu.className = "lumir-slash";
  menu.style.position = "absolute";
  menu.style.display = "none";
  document.body.appendChild(menu);

  let open = false;
  let items = [];
  let idx = 0;
  let block = null;

  function close() {
    open = false;
    block = null;
    menu.style.display = "none";
    menu.innerHTML = "";
  }
  // 뷰포트 인식 배치 — 하단 공간 없으면 블록 위로 flip, 우측 넘치면 좌로 shift, 최소 8px 여백 클램프.
  function position(b) {
    const content = (b && b.querySelector && b.querySelector(".content")) || b;
    const r = content && content.getBoundingClientRect ? content.getBoundingClientRect() : { left: 0, top: 0, bottom: 0 };
    menu.style.display = "block"; // 측정 전 표시(offsetWidth/Height 유효화)
    const mw = menu.offsetWidth || 300;
    const mh = menu.offsetHeight || 0;
    const vw = (document.documentElement && document.documentElement.clientWidth) || window.innerWidth || 0;
    const vh = (document.documentElement && document.documentElement.clientHeight) || window.innerHeight || 0;
    let left = r.left || 0;
    if (vw && left + mw > vw - 8) left = vw - mw - 8;
    if (left < 8) left = 8;
    let top = (r.bottom || 0) + 4;
    if (vh && mh && top + mh > vh - 8) {
      const up = (r.top || 0) - mh - 4;
      top = up >= 8 ? up : Math.max(8, vh - mh - 8);
    }
    menu.style.left = window.scrollX + left + "px";
    menu.style.top = window.scrollY + top + "px";
  }
  function render() {
    menu.style.display = open ? "block" : "none";
    if (!items.length) {
      menu.innerHTML = '<div class="lumir-slash-empty">결과 없음</div>';
      if (open && block) position(block);
      return;
    }
    let html = "";
    let lastGroup = null;
    items.forEach((it, i) => {
      const g = it.group || "기본 블록";
      if (g !== lastGroup) { html += `<div class="lumir-slash-group">${esc(g)}</div>`; lastGroup = g; }
      // 아이콘: 항목이 직접 SVG(it.ico가 "<svg…")를 주면 우선, 아니면 타입별 blocknote SVG(BLOCK_ICON),
      // 그래도 없으면(제목 등) 텍스트 글리프. → 모든 슬래시 아이콘이 monochrome SVG로 통일.
      const rawIco = it.ico || "";
      const iconInner = (typeof rawIco === "string" && rawIco.trim().toLowerCase().startsWith("<svg"))
        ? rawIco
        : (BLOCK_ICON[it.type] || esc(rawIco));
      const ico = `<span class="lumir-slash-ico">${iconInner}</span>`;
      const sub = it.sub ? `<span class="lumir-slash-sub">${esc(it.sub)}</span>` : "";
      // 단축키 배지: blocknote 형식(CTRL-ALT-1) — 하이픈 결합 + CSS 대문자.
      const sc = it.sc ? `<span class="lumir-slash-sc">${esc(it.sc.replace(/\s*\+\s*/g, "-"))}</span>` : "";
      html += `<div class="lumir-slash-item${i === idx ? " sel" : ""}" data-i="${i}">${ico}<span class="lumir-slash-body"><span class="lumir-slash-title">${esc(it.label)}</span>${sub}</span>${sc}</div>`;
    });
    menu.innerHTML = html;
    // 크기 확정 후 뷰포트 인식 재배치(내용에 따라 flip/shift)
    if (open && block) position(block);
    // 선택 항목이 보이도록 스크롤
    const selEl = menu.querySelector(".lumir-slash-item.sel");
    if (selEl && selEl.scrollIntoView) selEl.scrollIntoView({ block: "nearest" });
  }
  function filter(q) {
    // 공백 무시 검색: 질의·건초더미 양쪽에서 모든 공백 제거 후 부분일치("제목1"↔"제목 1" 동일)
    const nq = (q || "").toLowerCase().replace(/\s+/g, "");
    items = baseItems.concat(extraItems).filter((it) => {
      if (!nq) return true;
      const hay = (it.label + " " + (it.kw || "") + " " + (it.sub || "")).toLowerCase().replace(/\s+/g, "");
      return hay.includes(nq);
    });
    // 그룹 안정 정렬(첫 등장 순서 유지 — 같은 그룹끼리 모음)
    const order = [];
    items.forEach((it) => { const g = it.group || "기본 블록"; if (!order.includes(g)) order.push(g); });
    items.sort((a, b) => order.indexOf(a.group || "기본 블록") - order.indexOf(b.group || "기본 블록"));
    if (idx >= items.length) idx = 0;
    render();
  }
  function apply(it) {
    if (!it || !block) {
      close();
      return;
    }
    if (typeof it.action === "function") {
      const b = block;
      const c = b.querySelector && b.querySelector(".content");
      if (c) {
        c.innerHTML = ""; // "/명령" 텍스트 제거
        // 캐럿을 빈 블록에 복원 → 액션(이모지/파일 패널)이 올바른 위치·삽입 지점을 잡도록
        try { const r = document.createRange(); r.selectNodeContents(c); r.collapse(true); const s = window.getSelection(); s.removeAllRanges(); s.addRange(r); c.focus(); } catch (_) {}
      }
      close();
      it.action(b);
      onApply();
      return;
    }
    changeBlockType(block, it.type, propsForType(it.type, it.level, it.isToggle));
    close();
    onApply();
  }

  function onInput(e) {
    // IME 조합 중에도 필터해야 함(한글 "/제목" 입력 시 목록 갱신). 조합 가드는
    // Enter/Arrow 가로채기(onKeydownCapture)에만 적용 — 여기서 막으면 검색이 안 됨.
    const content = e.target.closest && e.target.closest(".content");
    if (!content || !editorEl.contains(content) || (content.closest && content.closest(".lumir-tbl"))) {
      if (open) close();
      return;
    }
    const b = content.closest(".block");
    if (!b || b.dataset.type !== "paragraph") {
      if (open) close();
      return;
    }
    const m = /^\/(.*)$/.exec(content.textContent); // 공백 포함 다중어 검색 허용
    if (m) {
      if (!open) {
        if (isComposing()) return; // 조합 중엔 새로 "열지" 않음(IME 가드). 단, 이미 열려있으면 아래 filter는 진행 → 한글 검색 동작
        open = true;
        block = b;
        idx = 0;
        position(b);
      }
      filter(m[1]); // 이미 열려 있으면 조합 중에도 필터(한글 /제목)
    } else if (open) {
      close();
    }
  }

  // capture 단계 — 메뉴 열렸을 때만 가로채 editing.js(Enter 분할)보다 우선
  function onKeydownCapture(e) {
    if (!open) return;
    if (e.isComposing || isComposing()) return; // IME 조합 중 Enter/Arrow는 가로채지 않음(한글 검색 선택 보호)
    if (e.key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();
      if (items.length) {
        idx = (idx + 1) % items.length;
        render();
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      if (items.length) {
        idx = (idx - 1 + items.length) % items.length;
        render();
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (items.length) apply(items[idx]);
      else close();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  }

  function onMousedown(e) {
    const it = e.target.closest && e.target.closest(".lumir-slash-item");
    if (it) {
      e.preventDefault();
      apply(items[+it.dataset.i]);
    }
  }

  editorEl.addEventListener("input", onInput);
  editorEl.addEventListener("keydown", onKeydownCapture, true); // capture
  menu.addEventListener("mousedown", onMousedown);

  return {
    isOpen: () => open,
    close,
    destroy() {
      editorEl.removeEventListener("input", onInput);
      editorEl.removeEventListener("keydown", onKeydownCapture, true);
      if (menu.parentNode) menu.parentNode.removeChild(menu);
    },
  };
}
